import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Logo from "../components/layout/utils/logo.svg";
import TextField from '@mui/material/TextField';
import { Box, Link, Grid, IconButton, InputAdornment } from '@mui/material';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import '../styles/Login.css'
import { AppRoutes } from '../axios/endPoints';
import Loader from '../components/common/Loader';
import { useEffect, useState } from 'react';
import { EndPoints } from '../axios/endPoints';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../axios/axiosHandler';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';

const schema = (t: Function) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('translations:FORGOT_PASSWORD_VALIDATION_EMAIL'))
      .required(t('translations:FORGOT_PASSWORD_VALIDATION_EMAIL_REQUIRED')),
  });

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      email: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const forgotPassword = (data: any) => {
    setLoading(true);

    const returnAddress = new URL(
      AppRoutes.RESET_PASSWORD,
      window.location.protocol + '//' + window.location.host
    ).href;

    axiosInstance
      .post(EndPoints.REQUEST_PASSWORD_RESET, {
        changePasswordUrl: returnAddress,
        email: data.email,
      })
      .then((payload) => {
        setLoading(false);
        setSuccess(payload.data.success);
        setError(payload.data.message);
      });
  };

  const emailSent = () => {
    return (
      <Box sx={{ mt: 2, width: '100%' }}>
        <Typography component="p">
          {t('translations:FORGOT_PASSWORD_RECOVERY_EMAIL')}
        </Typography>
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Link href={AppRoutes.LOG_IN} variant="body2">
            {t('translations:FORGOT_PASSWORD_GO_TO_EMAIL')}
          </Link>
        </Grid>
      </Box>
    );
  };

  const sendRecoveryEmail = () => {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit(forgotPassword)}
        sx={{ mt: 1, width: '100%' }}
      >
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error }}) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              placeholder={t('translations:FORGOT_PASSWORD_EMAIL_ADDRESS')}
              variant="outlined"
              type="email"
              error={!!error}
              helperText={error ? errors.email?.message : ''}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EmailTwoToneIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Collapse in={error != null}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="error"
          >
            {t(`translations:${error}`)}
          </Alert>
        </Collapse>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          className="button-color"
          sx={{ mt: 3, mb: 2 }}
        >
          {t('translations:FORGOT_PASSWORD_REQUEST_CHANGE')}
        </Button>
      </Box>
    );
  };

  useEffect(() => {
    document.title = t('translations:FORGOT_PASSWORD_PAGE_TITLE');
  }, [])

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {t('translations:FORGOT_PASSWORD_RESET_PASSWORD_TITLE')}
        </Typography>
        <Loader open={loading} />
        {!success ? sendRecoveryEmail() : emailSent()}
      </Box>
    </Container>
  );
}
